import { QueryFactory } from '@lmt/shared-types';

import { getFromCacheOrRequest } from '@lmt/unicorn-shared/app/api/custom-hooks';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { convertFields } from './ConvertFieldsToDocumentVariables';
import {
  CustomCommandExecutedEventArgs,
  DXR,
  EventArgs,
  RichEdit,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import { activateTemplateButtonId } from './ActivateTemplateRichEditButton';

const addGDTHeaderButtonId = 'AddGDTHeaderButtonId';
export const gdtHeaderTitle = 'gdt.templates.header';

async function addGDTHeaderAsync(
  richEdit: RichEdit,
  queryClient: QueryClient,
  templateId?: number,
) {
  const templates = await getFromCacheOrRequest(
    queryClient,
    QueryFactory.GDTOutgoingTemplatesQuery.getQueryKey(),
    () => QueryFactory.GDTOutgoingTemplatesClient.get(),
  );
  const headerTemplateId = templates.find(
    (x) => x.title === gdtHeaderTitle,
  )!.id;

  if (templateId === headerTemplateId) {
    return;
  }

  const headerTemplate = await getFromCacheOrRequest(
    queryClient,
    QueryFactory.GDTOutgoingTemplatesQuery.getSingleQueryKey(headerTemplateId),
    () => QueryFactory.GDTOutgoingTemplatesClient.getSingle(headerTemplateId),
  );

  const documentProcessor = richEdit.createDocumentProcessor();
  const importDocumentPromise = new Promise<void>((resolve, reject) => {
    documentProcessor.importDocument(
      headerTemplate.content,
      DXR.DocumentFormat.OpenXml,
      (result) => {
        if (result) {
          resolve();
        } else {
          reject('Error importing Header/Footer template');
        }
      },
    );
  });
  await importDocumentPromise;
  const document = documentProcessor.document;
  // Variable -> {DOCVARIABLE XNNNN}>
  // remove all ">" after "}"
  const fixedText = document.getText().replace(/}>/g, '}');
  richEdit.document.insertText(0, fixedText);
  await convertFields(richEdit);
}

interface Props {
  richEdit: React.MutableRefObject<RichEdit>;
  templateId?: number;
}
export const AddGDTHeaderRichEditButton: React.FC<Props> = (props) => {
  const { richEdit, templateId } = props;
  const i18n = useTranslation();
  const queryClient = useQueryClient();
  useEffect(
    function AddButtonsToRibbon() {
      richEdit.current?.updateRibbon((ribbon) => {
        if (!ribbon) return;
        const filePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
        filePanel.insertItem(
          new DXR.RibbonButtonItem(
            addGDTHeaderButtonId,
            i18n.t('gdt.templates.add_header_button'),
            {
              showText: true,
            },
          ),
        );
      });

      return () => {
        if (richEdit.current?.isDisposed === false) {
          richEdit.current?.updateRibbon((ribbon) => {
            if (!ribbon) return;
            const filePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
            filePanel.removeItem(addGDTHeaderButtonId);
          });
        }
      };
    },
    [templateId],
  );

  const addGDTHeaderMenuItem = useCallback(
    async (sender: RichEdit | undefined, _e: EventArgs | undefined) => {
      if (!sender) {
        return;
      }
      const lastRibbonSettings = (sender as any)._native.lastRibbonSettings;
      const filePanel = lastRibbonSettings.getTab(DXR.RibbonTabType.File)!;
      const addGDTHeaderMenuItem = filePanel.getItem(addGDTHeaderButtonId);
      const templates = await getFromCacheOrRequest(
        queryClient,
        QueryFactory.GDTOutgoingTemplatesQuery.getQueryKey(),
        () => QueryFactory.GDTOutgoingTemplatesClient.get(),
      );
      const headerTemplateId = templates.find(
        (x) => x.title === gdtHeaderTitle,
      )!.id;
      if (templateId !== headerTemplateId && !addGDTHeaderMenuItem) {
        sender.updateRibbon((ribbon) => {
          if (!ribbon) return;
          const newFilePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
          newFilePanel.insertItemBefore(
            new DXR.RibbonButtonItem(
              addGDTHeaderButtonId,
              i18n.t('gdt.templates.add_header_button'),
              {
                showText: true,
              },
            ),
            activateTemplateButtonId,
          );
        });
      }
      if (addGDTHeaderMenuItem && templateId === headerTemplateId) {
        sender.updateRibbon((ribbon) => {
          if (!ribbon) return;
          const newFilePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
          newFilePanel.removeItem(addGDTHeaderButtonId);
        });
      }
    },
    [templateId],
  );

  const addGDTHeaderHandler = useCallback(
    async (
      sender: RichEdit | undefined,
      args: CustomCommandExecutedEventArgs | undefined,
    ) => {
      if (!sender || !args) {
        return;
      }

      if (args.commandName === addGDTHeaderButtonId) {
        await addGDTHeaderAsync(richEdit.current, queryClient, templateId);
      }
    },
    [templateId],
  );

  useEffect(
    function AddHandlerForButtons() {
      richEdit.current.events.customCommandExecuted.addHandler(
        addGDTHeaderHandler,
      );
      return () => {
        richEdit.current.events.customCommandExecuted.removeHandler(
          addGDTHeaderHandler,
        );
      };
    },
    [addGDTHeaderHandler],
  );

  useEffect(
    function ShowHideButtonOnDocumentLoad() {
      const richEditCurrent = richEdit?.current;

      const handler = addGDTHeaderMenuItem;
      richEditCurrent.events.documentLoaded.addHandler(handler);
      return () => {
        richEditCurrent.events.documentLoaded.removeHandler(handler);
      };
    },
    [addGDTHeaderMenuItem],
  );

  return null;
};
